import { Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { ClassifiedDocumentType, DocumentTypes } from '@src/types/constants'
import { useTranslation } from 'react-i18next'

interface ReportFiltersProps {
  startDate: Date | null
  endDate: Date | null
  productLine: string
  classifiedDocumentType?: ClassifiedDocumentType
  onStartDateChange: (date: Date | null) => void
  onEndDateChange: (date: Date | null) => void
  onProductLineChange: (value: string) => void
  onClassifiedDocumentTypeChange: (value?: ClassifiedDocumentType) => void
  onGenerate: () => void
  onReset: () => void
  isPending: boolean
}

const ReportFilters = ({
  startDate,
  endDate,
  productLine,
  classifiedDocumentType,
  onStartDateChange,
  onEndDateChange,
  onProductLineChange,
  onClassifiedDocumentTypeChange,
  onGenerate,
  onReset,
  isPending,
}: ReportFiltersProps) => {
  const { t } = useTranslation()

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('reports.filters')}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <DatePicker
          label={t('reports.startDate')}
          value={startDate}
          onChange={onStartDateChange}
          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
        />
        <DatePicker
          label={t('reports.endDate')}
          value={endDate}
          onChange={onEndDateChange}
          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
        />
        <FormControl fullWidth size="small">
          <InputLabel>{t('reports.productLine')}</InputLabel>
          <Select
            value={productLine}
            onChange={(e) => onProductLineChange(e.target.value)}
            label={t('reports.productLine')}
          >
            <MenuItem value="">{t('common.reset')}</MenuItem>
            <MenuItem value="0">{t('enum.productLine.fa')}</MenuItem>
            <MenuItem value="1">{t('enum.productLine.ifp')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>{t('common.documentType')}</InputLabel>
          <Select
            value={classifiedDocumentType}
            onChange={(e) => onClassifiedDocumentTypeChange(e.target.value as ClassifiedDocumentType)}
            label={t('common.documentType')}
          >
            <MenuItem value={undefined}>{t('common.reset')}</MenuItem>
            <MenuItem value={DocumentTypes.Contract}>{t('enum.documentTypes.contract')}</MenuItem>
            <MenuItem value={DocumentTypes.Dcc}>{t('enum.documentTypes.dcc')}</MenuItem>
            <MenuItem value={DocumentTypes.Engagement}>{t('enum.documentTypes.engagement')}</MenuItem>
            <MenuItem value={DocumentTypes.RepayCapacity}>{t('enum.documentTypes.repaycapacity')}</MenuItem>
            <MenuItem value={DocumentTypes.Annex}>{t('enum.documentTypes.annex')}</MenuItem>
            <MenuItem value={DocumentTypes.Paystub}>{t('enum.documentTypes.paystub')}</MenuItem>
            <MenuItem value={DocumentTypes.ProofIncome}>{t('enum.documentTypes.proofincome')}</MenuItem>
            <MenuItem value={DocumentTypes.VoidCheck}>{t('enum.documentTypes.voidcheck')}</MenuItem>
            <MenuItem value={DocumentTypes.BankStatement}>{t('enum.documentTypes.bankstatement')}</MenuItem>
            <MenuItem value={DocumentTypes.CreditFile}>{t('enum.documentTypes.creditfile')}</MenuItem>
            <MenuItem value={DocumentTypes.DriverLicence}>{t('enum.documentTypes.driverlicence')}</MenuItem>
            <MenuItem value={DocumentTypes.IdPhoto}>{t('enum.documentTypes.idphoto')}</MenuItem>
            <MenuItem value={DocumentTypes.WorkPermit}>{t('enum.documentTypes.workpermit')}</MenuItem>
            <MenuItem value={DocumentTypes.Carfax}>{t('enum.documentTypes.carfax')}</MenuItem>
            <MenuItem value={DocumentTypes.Bankruptcy}>{t('enum.documentTypes.bankruptcy')}</MenuItem>
            <MenuItem value={DocumentTypes.OtherContract}>{t('enum.documentTypes.othercontract')}</MenuItem>
            <MenuItem value={DocumentTypes.Other}>{t('enum.documentTypes.others')}</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={onGenerate} disabled={isPending}>
            {t('reports.generate')}
          </Button>
          <Button variant="outlined" onClick={onReset} disabled={isPending}>
            {t('common.reset')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default ReportFilters
